import type { DemoState } from '../state';
import { onLocaleChange, t } from '../i18n';
import { isIpadLike } from '../util/ipad-detection';
import {
  createGpuDiagnosticsPayload,
  type GpuDiagnosticsInput,
  type GpuDiagnosticsPayload,
} from '@amitone/dither-core';

export type GpuDiagnosticState = Pick<DemoState, 'gpuEnabled' | 'gpuEnabledForSession'>;

export interface GpuDiagnosticIndicatorOptions {
  getState(): GpuDiagnosticState;
  /** Optional accessor for the full runtime GPU diagnostics input. When
   * provided, the diagnostics popup displays real hardware data instead of
   * the "no-renderer" fallback. */
  getGpuDiagnosticsInput?: () => GpuDiagnosticsInput;
  reinitialiseGpu?: () => void;
  isIpadLike?: () => boolean;
}

/** Build a "no-renderer available" diagnostics input from live state. Used
 * when no GPU renderer is wired (WebGL2 unavailable or GPU disabled). */
function buildNoRendererInput(state: GpuDiagnosticState): GpuDiagnosticsInput {
  return {
    userAgent: navigator.userAgent,
    isIpadLike: isIpadLike(),
    gpuEnabled: state.gpuEnabled,
    available: false,
    caps: null,
    contextLoss: { contextLossCount: 0, lastContextLossTimestamp: null },
    currentCapabilitySet: [],
    lastShaderCompileError: null,
  };
}

export function generateGpuDiagnosticsPayload(
  state: GpuDiagnosticState,
  getInput?: () => GpuDiagnosticsInput,
): GpuDiagnosticsPayload {
  const input = getInput ? getInput() : buildNoRendererInput(state);
  return createGpuDiagnosticsPayload(input);
}

export async function copyGpuDiagnosticsToClipboard(
  state: GpuDiagnosticState,
  getInput?: () => GpuDiagnosticsInput,
): Promise<void> {
  const payload = JSON.stringify(generateGpuDiagnosticsPayload(state, getInput), null, 2);
  await navigator.clipboard?.writeText?.(payload);
}

let closeGpuDiagnosticsPopup: (() => void) | null = null;

export function openGpuDiagnosticsPopup(
  anchor: HTMLElement,
  state: GpuDiagnosticState,
  getInput?: () => GpuDiagnosticsInput,
): void {
  closeGpuDiagnosticsPopup?.();

  const popup = document.createElement('div');
  popup.className = 'settings-popup gpu-diagnostics-popup';
  popup.setAttribute('role', 'dialog');
  popup.setAttribute('aria-modal', 'false');
  popup.setAttribute('aria-label', t('help.gpu.diagnostics.menuItem'));

  const title = document.createElement('div');
  title.className = 'settings-popup-label';
  title.textContent = t('help.gpu.diagnostics.menuItem');
  popup.appendChild(title);

  const diagnostics = document.createElement('pre');
  diagnostics.className = 'gpu-diagnostics-output';
  diagnostics.dataset['gpuDiagnosticsOutput'] = 'true';
  diagnostics.hidden = false;
  diagnostics.textContent = JSON.stringify(generateGpuDiagnosticsPayload(state, getInput), null, 2);
  popup.appendChild(diagnostics);

  document.body.appendChild(popup);

  const anchorRect = anchor.getBoundingClientRect();
  const popupRect = popup.getBoundingClientRect();
  let left = anchorRect.left;
  let top = anchorRect.bottom + 4;
  if (left + popupRect.width > window.innerWidth - 8) {
    left = window.innerWidth - popupRect.width - 8;
  }
  if (top + popupRect.height > window.innerHeight - 8) {
    top = Math.max(8, anchorRect.top - popupRect.height - 4);
  }
  popup.style.cssText = [
    'position:fixed',
    `left:${Math.max(8, left)}px`,
    `top:${Math.max(8, top)}px`,
    'z-index:9001',
  ].join(';');

  function close(): void {
    if (closeGpuDiagnosticsPopup !== close) return;
    closeGpuDiagnosticsPopup = null;
    document.removeEventListener('click', handleOutside, true);
    document.removeEventListener('keydown', handleKey, true);
    popup.remove();
  }

  function handleOutside(event: MouseEvent): void {
    if (!popup.contains(event.target as Node) && event.target !== anchor) {
      close();
    }
  }

  function handleKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  }

  setTimeout(() => {
    document.addEventListener('click', handleOutside, true);
    document.addEventListener('keydown', handleKey, true);
  }, 0);

  closeGpuDiagnosticsPopup = close;
}

export function createGpuDiagnosticIndicator(
  options: GpuDiagnosticIndicatorOptions,
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'settings-popup-section gpu-diagnostic-indicator';
  root.dataset['gpuDiagnosticIndicator'] = 'true';

  const title = document.createElement('div');
  title.className = 'settings-popup-label';
  title.dataset['i18n'] = 'settings.gpu.diagnostic.title';
  title.textContent = t('settings.gpu.diagnostic.title');
  root.appendChild(title);

  const body = document.createElement('div');
  body.className = 'gpu-diagnostic-indicator-body';
  root.appendChild(body);

  function render(): void {
    title.textContent = t('settings.gpu.diagnostic.title');
    body.textContent = '';
    const isIpad = (options.isIpadLike ?? isIpadLike)();
    if (isIpad) {
      const text = document.createElement('span');
      text.dataset['i18n'] = 'settings.gpu.diagnostic.reloadToRetry';
      text.textContent = t('settings.gpu.diagnostic.reloadToRetry');
      body.appendChild(text);
    } else {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'btn';
      retry.dataset['gpuRetry'] = 'true';
      retry.dataset['i18n'] = 'settings.gpu.diagnostic.retry';
      retry.textContent = t('settings.gpu.diagnostic.retry');
      retry.addEventListener('click', () => options.reinitialiseGpu?.());
      body.appendChild(retry);
    }

    const copy = document.createElement('button');
    copy.type = 'button';
    copy.className = 'btn';
    copy.dataset['gpuCopyDiagnostics'] = 'true';
    copy.dataset['i18n'] = 'help.gpu.diagnostics.menuItem';
    copy.textContent = t('help.gpu.diagnostics.menuItem');

    copy.addEventListener('click', () => {
      openGpuDiagnosticsPopup(copy, options.getState(), options.getGpuDiagnosticsInput);
    });
    body.appendChild(copy);
  }

  render();
  onLocaleChange(render);
  return root;
}
